import { ElementType } from '@fiverr-private/bianka_js';
import { reportClickBase, reportImpressionBase } from '../reportsBase';
import { EVENT_NAMES, type PageName, type SectionName } from './consts';

export interface BIEvents {
    reportSectionImpression: (attrs: { sectionName: SectionName }) => void;
    reportTestimonialImpression: (attrs: { testimonialId: string }) => void;
    reportCtaClick: (attrs: { componentName: string; label?: string }) => void;
    reportPositionImpression: (attrs: { positionUid: string; positionTitle: string }) => void;
    reportPositionClick: (attrs: { positionUid: string; positionTitle: string }) => void;
    reportFilterClick: (attrs: { filterType: string; filterValue: string | null }) => void;
    reportUnusualWayStepImpression: (attrs: { stepId: string }) => void;
    reportUnusualWayStepSubmit: (attrs: { stepId: string; maskedValue?: string }) => void;
    reportCvUploadStart: (attrs: { source: 'picker' }) => void;
    reportCvUploadFailed: (attrs: { stage: 'parsing' | 'matching' | 'not_a_cv'; reason?: string }) => void;
    reportCvUploadSuccess: (attrs: { matchedPositionUids: string[] }) => void;
    reportCvUploadNoMatch: () => void;
}

export const createEvents = (pageName: PageName): BIEvents => ({
    reportSectionImpression: ({ sectionName }) =>
        reportImpressionBase({
            eventName: EVENT_NAMES.sectionShown,
            pageName,
            componentName: sectionName,
        }),

    reportTestimonialImpression: ({ testimonialId }) =>
        reportImpressionBase({
            eventName: EVENT_NAMES.testimonialShown,
            pageName,
            componentName: 'testimonial_card',
            componentText: testimonialId,
        }),

    reportCtaClick: ({ componentName, label }) =>
        reportClickBase({
            eventName: EVENT_NAMES.ctaClick,
            pageName,
            componentName,
            componentType: ElementType.BUTTON,
            componentText: label,
        }),

    reportPositionImpression: ({ positionUid, positionTitle }) =>
        reportImpressionBase({
            eventName: EVENT_NAMES.positionShown,
            pageName,
            componentName: positionUid,
            componentText: positionTitle,
        }),

    reportPositionClick: ({ positionUid, positionTitle }) =>
        reportClickBase({
            eventName: EVENT_NAMES.positionClick,
            pageName,
            componentName: positionUid,
            componentType: ElementType.LINK,
            componentText: positionTitle,
        }),

    reportFilterClick: ({ filterType, filterValue }) =>
        reportClickBase({
            eventName: EVENT_NAMES.filterClick,
            pageName,
            componentName: filterType,
            componentType: ElementType.POPUP,
            componentText: filterValue ?? 'all',
        }),

    reportUnusualWayStepImpression: ({ stepId }) =>
        reportImpressionBase({
            eventName: EVENT_NAMES.unusualWayStepShown,
            pageName,
            componentName: `unusual_way_step_${stepId}`,
        }),

    reportUnusualWayStepSubmit: ({ stepId, maskedValue }) =>
        reportClickBase({
            eventName: EVENT_NAMES.unusualWayStepSubmit,
            pageName,
            componentName: `unusual_way_step_${stepId}`,
            componentType: ElementType.BUTTON,
            componentText: maskedValue,
        }),

    reportCvUploadStart: ({ source }) =>
        reportClickBase({
            eventName: EVENT_NAMES.cvUploadStart,
            pageName,
            componentName: `cv_upload_${source}`,
        }),

    reportCvUploadFailed: ({ stage, reason }) =>
        reportImpressionBase({
            eventName: EVENT_NAMES.cvUploadFailed,
            pageName,
            componentName: `cv_upload_failed_${stage}`,
            componentText: reason,
        }),

    // One reportActivity impression per matched position (mirrors Fiverr's Dynamic Matching BI
    // convention for match-candidate events) — bianka has no extension for a repeated-id payload,
    reportCvUploadSuccess: ({ matchedPositionUids }) =>
        matchedPositionUids.forEach((positionUid) =>
            reportImpressionBase({
                eventName: EVENT_NAMES.cvUploadSuccess,
                pageName,
                componentName: 'cv_upload_success',
                componentText: positionUid,
            })
        ),

    reportCvUploadNoMatch: () =>
        reportImpressionBase({
            eventName: EVENT_NAMES.cvUploadNoMatch,
            pageName,
            componentName: 'cv_upload_no_match',
        }),
});
